import React, { useEffect, useState } from 'react';
import { Animated, LayoutAnimation, Platform, View } from 'react-native';
import { Button, Props as ButtonProps } from '@/components/button';
import { createStyles } from '@/contexts/theme-context';
import { LinearGradient } from 'expo-linear-gradient';
import { TrackingData } from '@/utils/analytics/base-reporter';
import { Typography, TypographyProps } from './typography';

interface Props extends Omit<TypographyProps, 'style'> {
  buttonContainerStyle?: ButtonProps['containerStyle'];
  buttonStyle?: ButtonProps['style'];
  collapsedCopy: string;
  expandedCopy: string;
  numberOfLines: number;
  trackingData?: TrackingData;
}

export const ExpandingTypography: React.FC<Props> = ({
  numberOfLines,
  children,
  expandedCopy,
  collapsedCopy,
  buttonContainerStyle,
  buttonStyle,
  trackingData,
  ...rest
}) => {
  const [styles, theme] = useStyles();
  const [canExpand, setCanExpand] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // e.nativeEvent.lines does not exist on web so set text to always be expanded
  useEffect(() => {
    if (Platform.OS === 'web') {
      setCanExpand(true);
      setExpanded(true);
    }
  }, [children, numberOfLines]);

  const shouldUseExpandableContainer = canExpand && Platform.OS !== 'web';

  const handleToggleExpanded = () => {
    // configure the layout animation before state change
    LayoutAnimation.configureNext({
      duration: 400,
      update: {
        ...LayoutAnimation.Presets.spring.update,
        springDamping: 0.9,
        type: LayoutAnimation.Types.spring,
      },
    });

    setExpanded((e) => !e);
  };

  return (
    <View
      style={shouldUseExpandableContainer ? styles.expandableContainer : null}
    >
      <Animated.View>
        <Typography {...rest} numberOfLines={expanded ? 0 : numberOfLines}>
          {children}
        </Typography>
      </Animated.View>
      {Platform.OS !== 'web' ? (
        <View accessible={false} style={styles.invisibleTextContainer}>
          <Typography
            {...rest}
            onTextLayout={(e) => {
              // Render invisible text to measure total number of lines
              setCanExpand(e.nativeEvent.lines.length > numberOfLines);
            }}
          >
            {children}
          </Typography>
        </View>
      ) : null}
      {canExpand && Platform.OS !== 'web' ? (
        <View style={styles.expandableSection}>
          {!expanded ? (
            <LinearGradient
              colors={[
                `${theme.sk.color.text.ondark}00`,
                theme.sk.color.text.ondark,
              ]}
              end={[0, 1]}
              start={[0, 0]}
              style={styles.collapsedGradient}
            />
          ) : null}
          <Animated.View>
            <Button
              containerStyle={[
                styles.expandButtonContainer,
                buttonContainerStyle,
              ]}
              onPress={handleToggleExpanded}
              size="xsmall"
              style={[styles.expandButton, buttonStyle]}
              trackingData={trackingData ?? null}
              type="tertiary"
            >
              {expanded ? expandedCopy : collapsedCopy}
            </Button>
          </Animated.View>
        </View>
      ) : null}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  collapsedGradient: {
    height: 40,
  },
  expandableContainer: {
    overflow: 'hidden',
    paddingBottom: 28,
  },
  expandableSection: {
    bottom: 0,
    left: 0,
    position: 'absolute',
    right: 0,
  },
  expandButton: {
    paddingHorizontal: 0,
  },
  expandButtonContainer: {
    alignSelf: 'flex-start',
    backgroundColor: theme.colors.white,
  },
  invisibleTextContainer: {
    opacity: 0,
    position: 'absolute',
  },
}));
